<template>
  <div :class="'crosses-tree'" v-if="props.crosses && props.crosses.length">
    <h2 class="divider-sec">
      {{ props.strain.name }} strain crosses    
    </h2>



    <UiHorizontalScroll
      :arrows="true"
      :arrows-show-hover="true"
      v-if="props.crosses"
      >

    <div class="crosses-container">
      <div 
        class="cross-tree-item" 
        v-for="(cross, index) in props.crosses" 
        :key="`cross-${index}`"
      >
        <div class="custom-tree">
          <div class="tree-structure">

            <!-- Child column -->
            <div class="child-column">
              <div class="child-node">
                <div v-if="cross.child.is_hidden || cross.child.is_removed" class="result-node">
                  <img loading="lazy" :src="cross.child.cover_s ? cross.child.cover_s : '/images/strain-cover-empty.svg'" class="cover" :alt="cross.child.name" />
                  <span class="lbl">{{ cross.child.name }}</span>
                </div>
                <NuxtLink v-else class="result-node" :to="cross.child.link">
                  <img loading="lazy" :src="cross.child.cover_s ? cross.child.cover_s : '/images/strain-cover-empty.svg'" class="cover" :alt="cross.child.name" />
                  <span class="lbl">{{ cross.child.name }}</span>
                </NuxtLink>
              </div>
            </div>


            <!-- Connecting lines -->
            <div class="connection-area">
              <div class="connection-lines">
                <div class="line line-1"></div>
                <div class="line line-2"></div>
                <div class="line line-horizontal"></div>
                <div class="line line-to-child"></div>
              </div>
            </div>


            <!-- Parents column -->
            <div class="parents-column">
              <!-- Target parent -->
              <div class="parent-node">
                <div v-if="cross.target.is_hidden || cross.target.is_removed" class="nd">
                  <img loading="lazy" v-if="cross.target.cover_s" :src="cross.target.cover_s" class="cover" :alt="cross.target.name" />
                  <span class="lbl">{{ cross.target.name }}</span>
                </div>
                <NuxtLink v-else class="nd" :to="cross.target.link">
                  <img loading="lazy" v-if="cross.target.cover_s" :src="cross.target.cover_s" class="cover" :alt="cross.target.name" />
                  <span class="lbl">{{ cross.target.name }}</span>
                </NuxtLink>
              </div>
              
              <!-- Parent parent -->
              <div class="parent-node">
                <div v-if="cross.parent.is_hidden || cross.parent.is_removed" class="nd">
                  <img loading="lazy" v-if="cross.parent.cover_s" :src="cross.parent.cover_s" class="cover" :alt="cross.parent.name" />
                  <span class="lbl">{{ cross.parent.name }}</span>
                </div>
                <NuxtLink v-else class="nd" :to="cross.parent.link">
                  <img loading="lazy" v-if="cross.parent.cover_s" :src="cross.parent.cover_s" class="cover" :alt="cross.parent.name" />
                  <span class="lbl">{{ cross.parent.name }}</span>
                </NuxtLink>
              </div>
            </div>
            
            
          </div>
        </div>
      </div>
    </div>
  </UiHorizontalScroll>
  </div>
</template>

<script setup lang="ts">
import type { StrainCross, Strain } from '@/types/strain'

interface Props {
  title?: string;
  strain: Strain;
  crosses: StrainCross[];
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Crosses'
})
</script>

<style scoped>
.crosses-tree {
  margin-bottom: 2rem;
}

.crosses-container {
  display: flex;
  flex-direction: row;
  gap: 0 1rem;
  /* overflow-x: auto; */
}

.cross-tree-item {
  /* background-color: var(--un-background-color-gray); */
  border-radius: 15px;
  padding: 1rem;
  flex-shrink: 0;
}

/* Custom tree structure */
.tree-structure {
  display: flex;
  align-items: center;
  gap: 0rem;
  min-height: 120px;
}

.parents-column {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 200px;
}

.parent-node {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.connection-area {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 160px;
  position: relative;
  flex-shrink: 0;
}

.connection-lines {
  position: relative;
  width: 100%;
  height: 100%;
}

/* Connection lines */
.line {
  position: absolute;
  background-color: #ddd;
}

.line-1 {
  /* Line from first parent */
  top: 25%;
  left: 18px;
  width: 20px;
  height: 1px;
}

.line-2 {
  /* Line from second parent */
  top: 75%;
  left: 18px;
  width: 20px;
  height: 1px;
}

.line-horizontal {
  /* Vertical connecting line */
  top: 25%;
  left: 18px;
  width: 1px;
  height: 50%;
}

.line-to-child {
  /* Line to child */
  top: 50%;
  left: -102px;
  width: 120px;
  height: 1px;
  background-color: transparent;
  background: linear-gradient(to right, transparent, #ddd);
}

.child-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  
}

.child-node {
  display: flex;
  align-items: center;
}

/* Node styles - matching BrandTreeHorizontal */
.nd {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 22px 8px 12px;
  border-radius: 100px;
  background-color: var(--un-background-color-gray);
  text-decoration: none;
  color: var(--color-text);
  transition: opacity 0.2s;
}

.nd:hover {
  opacity: 0.8;
}

.nd .cover {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 10px;
  object-fit: contain;
  background-color: white;
  padding: 5px;
  flex-shrink: 0;
}

.nd .lbl {
  font-weight: bold;
  color: var(--color-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Result node styles - same as regular nodes */
.result-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;

}
.result-node .cover{
  aspect-ratio: 77/120;
  height: 220px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}
.result-node .lbl{
  font-weight: bold;
  text-align: center;
  line-height: 1rem;
}
/* Mobile responsive */
@container pb (max-width: 650px) {
  /* .crosses-container {
    flex-direction: column;
    overflow-x: auto;
  }  */
}
 
</style>
